import { motion } from 'framer-motion'
import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react'
import { Card } from '../ui/Card'

interface StatCardProps {
  title: string
  value: string | number
  change?: number
  changeLabel?: string
  icon: LucideIcon
  color?: 'default' | 'brand' | 'green' | 'amber' | 'blue'
}

const colorSchemes = {
  default: 'bg-dark-bg-tertiary text-dark-text-primary',
  brand: 'bg-brand-500/10 text-brand-400',
  green: 'bg-green-500/10 text-green-400',
  amber: 'bg-amber-500/10 text-amber-400',
  blue: 'bg-blue-500/10 text-blue-400',
}

export const StatCard = ({ 
  title, 
  value, 
  change, 
  changeLabel,
  icon: Icon,
  color = 'brand'
}: StatCardProps) => {
  const colorClasses = colorSchemes[color]
  
  return (
    <Card padding="lg" hover>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-dark-text-secondary mb-1">{title}</p>
          <motion.h3 
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-2xl font-bold text-dark-text-primary"
          >
            {value}
          </motion.h3>
          
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-2">
              {change > 0 ? (
                <TrendingUp className="w-4 h-4 text-green-400" />
              ) : change < 0 ? (
                <TrendingDown className="w-4 h-4 text-red-400" />
              ) : null}
              <span className={`text-sm font-medium ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {change > 0 ? '+' : ''}{change}%
              </span>
              {changeLabel && (
                <span className="text-xs text-dark-text-muted">{changeLabel}</span>
              )}
            </div>
          )}
        </div>
        
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  )
}

export { TrendingUp, TrendingDown } from 'lucide-react'
